import React, { useEffect, useState, MouseEvent, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

import styles from './styles/GroupsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Group {
  group_id: number;
  name: string;
}

const GroupsPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const navigate = useNavigate();
  const hasTrackedRef = useRef(false);

  // Track page visit only once when component mounts
  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackPageVisit("Groups", "/groups");
      hasTrackedRef.current = true;
    }
  }, []);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/groups`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch groups.");
          return;
        }

        const data = await response.json();
        setGroups(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    fetchGroups();
  }, [token, selectedOrgID]);

  const filteredGroups = groups.filter((group) =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (groupID: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${groupID}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        console.error("Failed to delete group.");
        return;
      }

      setGroups(groups.filter((g) => g.group_id !== groupID));
      setGroupToDelete(null);
    } catch (err) {
      console.error("Error deleting group:", err);
    }
  };

  const navigateToCreate = () => {
    navigate("/groups/create");
  };

  if (error) {
    return <p style={{ color: "red" }}>{error}</p>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Groups</h1>

      <div className={styles.searchContainer}>
        <input
          type="text"
          id="search"
          className={styles.searchInput}
          placeholder="Search by group name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <button onClick={navigateToCreate} className={styles.createButton}>
        New Group
      </button>

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.tableHeader}>Group</th>
            <th className={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredGroups.length > 0 ? (
            filteredGroups.map((g) => (
              <tr key={g.group_id} className={styles.tableRow}>
                <td className={styles.tableCell}>
                  <a
                    href="#"
                    className={styles.tableLink}
                    onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                      e.preventDefault();
                      navigate(`/groups/${g.group_id}`);
                    }}
                  >
                    {g.name}
                  </a>
                </td>
                <td className={styles.tableCell}>
                  <button
                    onClick={() => setGroupToDelete(g)}
                    className={styles.deleteButton}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={2} className={styles.tableCell}>No groups found.</td>
            </tr>
          )}
        </tbody>
      </table>

      {groupToDelete && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setGroupToDelete(null)} />
          <div className={styles.dialog}>
            <p>
              Delete group {groupToDelete.name}?<br /><br />
              This action CANNOT be undone.
            </p>
            <div className={styles.dialogButtons}>
              <button onClick={() => handleDelete(groupToDelete.group_id)} className={styles.dialogButton}>
                Yes, Delete
              </button>
              <button onClick={() => setGroupToDelete(null)} className={styles.dialogCancelButton}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GroupsPage;
